/**
 * Inventory page — NetSuite inventory items for a single portfolio company.
 * In live mode: requires authentication via MsalAuthenticationTemplate.
 * In demo mode: loads data without authentication (uses demo endpoint with live NS fallback).
 */

import { useParams, Link } from "react-router-dom";
import { MsalAuthenticationTemplate, useMsal } from "@azure/msal-react";
import { InteractionStatus, InteractionType } from "@azure/msal-browser";
import { useCallback } from "react";
import { powerbiRequest } from "../authConfig";
import { Loading } from "../ui-components/Loading";
import { LoadingNotice } from "../ui-components/LoadingNotice";
import { ErrorComponent } from "../ui-components/ErrorComponent";
import { MetricCard } from "../ui-components/MetricCard";
import { InventoryTable } from "../ui-components/InventoryTable";
import { InventoryBalanceTable } from "../ui-components/InventoryBalanceTable";
import { PortcoSubNav } from "../ui-components/PortcoSubNav";
import { InventoryInsightsPanel } from "../ui-components/InventoryInsightsPanel";
import { isDemoMode, NetSuiteRow } from "../utils/PowerBIApiCall";
import { useInventoryData } from "../hooks/useInventoryData";
import { useInventoryInsights } from "../hooks/useInventoryInsights";
import {
  formatCurrencyCompact as formatCurrency,
  formatNumber,
} from "../utils/format";

// =============================================================================
// Helpers
// =============================================================================

/** Detect ARG-style inventory (has locationname/inventorynumber fields) vs Wellmore flat items */
function isBalanceData(items: NetSuiteRow[]): boolean {
  if (items.length === 0) return false;
  return "locationname" in items[0] || "inventorynumber" in items[0];
}

/** Group ARG inventory balance rows by itemid for the summary view */
export interface GroupedItem {
  itemid: string;
  displayname: string;
  totalOnHand: number;
  totalOnHandValue: number;
  avgDailyUnits90d: number | null;
  daysOnHand: number | null;
  locationCount: number;
  rows: NetSuiteRow[];
}

function groupByItem(rows: NetSuiteRow[]): GroupedItem[] {
  const map = new Map<string, GroupedItem>();

  for (const row of rows) {
    const key = String(row.itemid ?? "");
    let group = map.get(key);
    if (!group) {
      group = {
        itemid: key,
        displayname: String(row.displayname ?? ""),
        totalOnHand: 0,
        totalOnHandValue: 0,
        avgDailyUnits90d: null,
        daysOnHand: null,
        locationCount: 0,
        rows: [],
      };
      map.set(key, group);
    }
    const oh = Number(row.quantityonhand);
    if (!isNaN(oh)) group.totalOnHand += oh;
    const val = Number(row.onhandvalue);
    if (!isNaN(val)) group.totalOnHandValue += val;
    // avg_daily_units_90d is per-item (same across all rows), take first non-null
    if (group.avgDailyUnits90d == null && row.avg_daily_units_90d != null) {
      group.avgDailyUnits90d = Number(row.avg_daily_units_90d);
    }
    group.rows.push(row);
  }

  // Count distinct locations and compute item-level days on hand
  const groups = Array.from(map.values());
  for (const group of groups) {
    const locations = new Set(
      group.rows.map((r: NetSuiteRow) => String(r.locationname ?? "")),
    );
    group.locationCount = locations.size;
    // Recompute days on hand from total on hand / avg daily units
    if (group.avgDailyUnits90d && group.avgDailyUnits90d > 0) {
      group.daysOnHand =
        Math.round((group.totalOnHand / group.avgDailyUnits90d) * 10) / 10;
    }
  }

  return groups;
}

function computeSummary(items: NetSuiteRow[]) {
  if (isBalanceData(items)) {
    const grouped = groupByItem(items);
    let totalValue = 0;
    let totalOnHand = 0;
    for (const g of grouped) {
      totalValue += g.totalOnHandValue;
      totalOnHand += g.totalOnHand;
    }
    return { totalItems: grouped.length, totalValue, totalOnHand };
  }

  let totalItems = items.length;
  let totalValue = 0;
  let totalOnHand = 0;

  for (const item of items) {
    const val = Number(item.totalvalue);
    if (!isNaN(val)) totalValue += val;
    const oh = Number(item.quantityonhand);
    if (!isNaN(oh)) totalOnHand += oh;
  }

  return { totalItems, totalValue, totalOnHand };
}

// =============================================================================
// Shared UI — pure presentational component, no MSAL dependency
// =============================================================================

interface InventoryUIProps {
  items: NetSuiteRow[];
  portcoName: string | null;
  portcoId?: string;
  loading: boolean;
  error: string | null;
}

function InventoryUI({
  items,
  portcoName,
  portcoId,
  loading,
  error,
}: InventoryUIProps) {
  const insights = useInventoryInsights(portcoId);

  if (loading) {
    return (
      <div className="w-full max-w-6xl space-y-6">
        <LoadingNotice message="Loading inventory data — this can take a few seconds…" />
        {/* Skeleton breadcrumb */}
        <div className="flex items-center justify-between mb-2">
          <div className="h-4 w-32 bg-gray-200 rounded animate-pulse" />
        </div>
        {/* Skeleton header */}
        <div>
          <div className="h-8 w-64 bg-gray-200 rounded animate-pulse mb-2" />
          <div className="h-4 w-48 bg-gray-100 rounded animate-pulse" />
        </div>
        {/* Skeleton metric cards */}
        <div className="grid grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => (
            <div
              key={i}
              className="bg-white border border-gray-200/70 rounded-xl shadow-card p-5"
            >
              <div className="h-3 w-20 bg-gray-100 rounded animate-pulse mx-auto mb-3" />
              <div className="h-7 w-28 bg-gray-200 rounded animate-pulse mx-auto" />
            </div>
          ))}
        </div>
        {/* Skeleton table */}
        <div className="bg-white border border-gray-200/70 rounded-xl shadow-card overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <div className="h-5 w-40 bg-gray-200 rounded animate-pulse" />
          </div>
          {[...Array(5)].map((_, i) => (
            <div
              key={i}
              className="px-6 py-4 border-b border-gray-50 flex items-center gap-6"
            >
              <div className="flex-1 grid grid-cols-5 gap-4">
                {[...Array(5)].map((_, j) => (
                  <div
                    key={j}
                    className="h-4 bg-gray-100 rounded animate-pulse"
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full max-w-lg">
        <div className="bg-white border border-red-100 rounded-xl shadow-card overflow-hidden">
          <div className="p-6">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                <svg
                  className="w-4 h-4 text-red-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>
              <div>
                <h3 className="text-gray-900 font-semibold mb-1">
                  Something went wrong
                </h3>
                <p className="text-gray-500 text-sm">{error}</p>
              </div>
            </div>
          </div>
        </div>
        {portcoId && (
          <Link
            to={`/portco/${portcoId}`}
            className="inline-flex items-center gap-1.5 mt-4 text-sm text-gray-500 hover:text-wmp-500 transition-colors no-underline"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Back to {portcoName || "Detail"}
          </Link>
        )}
      </div>
    );
  }

  const summary = computeSummary(items);

  return (
    <div className="w-full max-w-6xl">
      {/* Breadcrumb + sub-nav */}
      <div className="mb-5 space-y-3">
        <Link
          to={portcoId ? `/portco/${portcoId}` : "/dashboard"}
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-wmp-500 transition-colors no-underline group"
        >
          <svg
            className="w-4 h-4 text-gray-400 group-hover:text-wmp-500 transition-colors"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          {portcoName || "Overview"}
        </Link>
        {portcoId && <PortcoSubNav portcoId={portcoId} />}
      </div>

      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900 tracking-tight">
          {portcoName ? `${portcoName} — Inventory` : "Inventory"}
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          NetSuite inventory items — {items.length.toLocaleString()} active
          items
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <MetricCard
          label="Total Items"
          value={formatNumber(summary.totalItems)}
          highlighted
        />
        <MetricCard
          label="Total Value"
          value={formatCurrency(summary.totalValue)}
        />
        <MetricCard label="On Hand" value={formatNumber(summary.totalOnHand)} />
      </div>

      {/* AI Insights — available for any portco with inventory data
          (ARG balance rows and Wellmore velocity-enriched items both qualify) */}
      {items.length > 0 && (
        <div className="mb-6">
          <InventoryInsightsPanel
            data={insights.data}
            loading={insights.loading}
            error={insights.error}
            onRun={insights.fetch}
          />
        </div>
      )}

      {/* Inventory Table */}
      {isBalanceData(items) ? (
        <InventoryBalanceTable groups={groupByItem(items)} />
      ) : (
        <InventoryTable items={items} />
      )}
    </div>
  );
}

// =============================================================================
// Demo mode data loader — no MSAL hooks
// =============================================================================

function DemoInventoryContent() {
  const { portcoId } = useParams<{ portcoId: string }>();
  const data = useInventoryData(portcoId);

  return (
    <InventoryUI
      items={data.items}
      portcoName={data.portcoName}
      portcoId={portcoId}
      loading={data.loading}
      error={data.error}
    />
  );
}

// =============================================================================
// Live mode data loader — uses MSAL hooks (must be inside MsalProvider)
// =============================================================================

function LiveInventoryContent() {
  const { portcoId } = useParams<{ portcoId: string }>();
  const { instance, inProgress } = useMsal();

  const handleAuthError = useCallback(() => {
    const { InteractionRequiredAuthError } = require("@azure/msal-browser");
    // Re-acquire token
    instance.acquireTokenRedirect({
      ...powerbiRequest,
      account: instance.getActiveAccount()!,
    });
  }, [instance]);

  const data = useInventoryData(
    portcoId,
    inProgress === InteractionStatus.None,
    handleAuthError,
  );

  return (
    <InventoryUI
      items={data.items}
      portcoName={data.portcoName}
      portcoId={portcoId}
      loading={data.loading}
      error={data.error}
    />
  );
}

// =============================================================================
// Exported Inventory component — routes to demo or live mode
// =============================================================================

export function Inventory() {
  if (isDemoMode()) {
    return <DemoInventoryContent />;
  }

  return (
    <MsalAuthenticationTemplate
      interactionType={InteractionType.Redirect}
      authenticationRequest={powerbiRequest}
      errorComponent={ErrorComponent}
      loadingComponent={Loading}
    >
      <LiveInventoryContent />
    </MsalAuthenticationTemplate>
  );
}
